import { z } from "zod/v4"
import { HomeBannerId } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { Period, PeriodState, PeriodRequest } from "./common"
import { DocumentStatus } from "../common"

export const HomeBannerDisplayTab = z.enum(["recommend", "following"])

export type HomeBannerDisplayTab = z.infer<typeof HomeBannerDisplayTab>

export const HomeBannerDisplayTabOptions = [
  { value: "recommend" as const, label: "おすすめ" },
  { value: "following" as const, label: "フォロー中" },
]

export const HomeBannerDocumentMeta = z.object({
  managed_image_id: z.string().describe("画像のID（ManagedImageId）"),
  title: z.string().describe("バナーのタイトル"),
  uri: z.string().describe("バナータップ時の遷移先URI"),
  priority: z.number().int().min(1).max(99999).describe("優先度（1-99999）"),
  display_slot: z.number().int().describe("表示枠番号"),
  display_tab: HomeBannerDisplayTab.describe("表示タブ（おすすめ/フォロー中）"),
  display_period: Period.describe("表示期間"),
  status: DocumentStatus.describe("バナーの状態"),
})

export type HomeBannerDocumentMeta = z.infer<typeof HomeBannerDocumentMeta>

export const HomeBannerDocumentBody = z.intersection(
  HomeBannerDocumentMeta,
  z.object({
    reference_managed_image_ids: z.array(z.string()).describe("使用している画像のIDリスト（自動計算）"),
    display_period_state: PeriodState.describe("表示期間の状態（システム自動計算）"),
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type HomeBannerDocumentBody = z.infer<typeof HomeBannerDocumentBody>

export type HomeBannerDocument = HomeBannerDocumentBody & {
  id: HomeBannerId,
}

export class HomeBannerDocuments {

  static toDocumentData(banner: HomeBannerDocument): object {
    return {
      ...banner,
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): HomeBannerDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: HomeBannerId(doc.id),
      managed_image_id: data["managed_image_id"],
      title: data["title"],
      uri: data["uri"],
      priority: data["priority"],
      display_slot: data["display_slot"],
      display_tab: data["display_tab"],
      display_period: data["display_period"],
      display_period_state: data["display_period_state"],
      reference_managed_image_ids: data["reference_managed_image_ids"] || [],
      status: data["status"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

export const CreateHomeBannerRequest = z.object({
  managedImageId: z.string().describe("画像のID（ManagedImageId）"),
  title: z.string().describe("バナーのタイトル"),
  uri: z.string().min(1, "URIは必須です").describe("バナータップ時の遷移先URI"),
  priority: z.number().int().min(1).max(99999).default(1).describe("優先度（1-99999）"),
  displaySlot: z.number().int().default(20).describe("表示枠番号"),
  displayTab: HomeBannerDisplayTab.describe("表示タブ（おすすめ/フォロー中）"),
  displayPeriod: PeriodRequest.describe("表示期間"),
  status: DocumentStatus.describe("バナーの状態"),
})

export type CreateHomeBannerRequest = z.infer<typeof CreateHomeBannerRequest>

export const UpdateHomeBannerRequest = z.object({
  managedImageId: z.string().optional().describe("画像のID（ManagedImageId）"),
  title: z.string().optional().describe("バナーのタイトル"),
  uri: z.string().min(1).optional().describe("バナータップ時の遷移先URI"),
  priority: z.number().int().min(1).max(99999).optional().describe("優先度（1-99999）"),
  displaySlot: z.number().int().optional().describe("表示枠番号"),
  displayTab: HomeBannerDisplayTab.optional().describe("表示タブ（おすすめ/フォロー中）"),
  displayPeriod: PeriodRequest.optional().describe("表示期間"),
  status: DocumentStatus.optional().describe("バナーの状態"),
})

export type UpdateHomeBannerRequest= z.infer<typeof UpdateHomeBannerRequest>
